import * as qs from 'qs';
import { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';

import { SEARCH_PARAMS } from 'components/search/InstantSearch/types';
import {
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_HOME,
  ANY_SPORT_MARKET_COMMON_PACKS,
  ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
  FOOTBALL_BUNDLED_AUCTIONS_ID,
  FOOTBALL_CLUBS_SLUG,
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_LEAGUES_SLUG,
  FOOTBALL_MARKET,
  FOOTBALL_MARKET_MANAGER_SALES,
  FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
  FOOTBALL_MARKET_NEW_SIGNINGS,
  FOOTBALL_ONBOARDING_CHOOSE_COMPETITION,
  FOOTBALL_ONBOARDING_LEADERBOARD_FIRST_LINEUP,
  FOOTBALL_ONBOARDING_TUTORIAL,
  LANDING,
  MLB_MARKET,
  MLB_MARKET_PRIMARY,
  MLB_MARKET_SECONDARY,
  MY_SORARE,
  MY_SORARE_OFFERS_RECEIVED,
  NBA_MARKET,
  NBA_MARKET_PRIMARY,
  NBA_MARKET_SECONDARY,
  NBA_ONBOARDING,
} from 'constants/__generated__/routes';
import { matchAnySportPath } from 'lib/routing/generateSportPath';

type InteractionContext =
  | 'competition_details'
  | 'landing'
  | 'landing_nba'
  | 'landing_epl'
  | 'homepage'
  | 'my_sorare'
  | 'my_sorare_offers_received'
  | 'my_club'
  | 'my_club_cards'
  | 'my_club_history'
  | 'user_gallery_collections'
  | 'primary_market'
  | 'secondary_market'
  | 'featured_market'
  | 'player_page'
  | 'card_page'
  | 'bundled_auction_page'
  | 'club_page'
  | 'country_page'
  | 'league_page'
  | 'onboarding'
  | 'compose_team'
  | 'lobby'
  | 'private_league_details'
  | 'club_honors'
  | 'club_shop'
  | 'common_pack_details'
  | 'common_pack_list'
  | string;

const getPathname = ({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): InteractionContext => {
  if (matchPath(LANDING, pathname)) {
    return 'landing';
  }
  if (matchAnySportPath(ANY_SPORT_HOME, pathname)) {
    return 'homepage';
  }
  if (matchPath(MY_SORARE_OFFERS_RECEIVED, pathname)) {
    return 'my_sorare_offers_received';
  }
  if (matchPath(catchAll(MY_SORARE), pathname)) {
    return 'my_sorare';
  }
  if (
    matchAnySportPath(
      catchAll(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION),
      pathname
    )
  ) {
    return 'user_gallery_collections';
  }
  if (
    matchPath(FOOTBALL_MARKET, pathname) ||
    matchPath(NBA_MARKET, pathname) ||
    matchPath(MLB_MARKET, pathname)
  ) {
    return 'market_home';
  }
  if (
    matchPath(FOOTBALL_MARKET_NEW_SIGNINGS, pathname) ||
    matchPath(MLB_MARKET_PRIMARY, pathname) ||
    matchPath(NBA_MARKET_PRIMARY, pathname)
  ) {
    return 'primary_market';
  }
  if (
    matchPath(FOOTBALL_MARKET_MANAGER_SALES, pathname) ||
    matchPath(MLB_MARKET_SECONDARY, pathname) ||
    matchPath(NBA_MARKET_SECONDARY, pathname)
  ) {
    const queryString = qs.parse(search, {
      ignoreQueryPrefix: true,
    });
    return queryString[SEARCH_PARAMS.UNSTACKED]
      ? 'secondary_market_unstacked'
      : 'secondary_market';
  }
  if (matchPath(FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY, pathname)) {
    return 'secondary_market_stack';
  }
  if (matchAnySportPath(catchAll(ANY_SPORT_PLAYERS_SLUG), pathname)) {
    return 'player_page';
  }
  if (matchAnySportPath(ANY_SPORT_CARDS_SLUG, pathname)) {
    return 'card_page';
  }
  if (matchPath(catchAll(FOOTBALL_CLUBS_SLUG), pathname)) {
    return 'club_page';
  }
  if (matchPath(FOOTBALL_COUNTRIES_COUNTRYCODE, pathname)) {
    return 'country_page';
  }
  if (matchPath(catchAll(FOOTBALL_LEAGUES_SLUG), pathname)) {
    return 'league_page';
  }
  if (matchPath(NBA_ONBOARDING, pathname)) {
    return 'onboarding';
  }
  if (matchPath(FOOTBALL_BUNDLED_AUCTIONS_ID, pathname)) {
    return 'bundled_auction_page';
  }

  if (matchPath(ANY_SPORT_PLAY, pathname)) {
    return 'lobby';
  }
  if (
    matchPath(catchAll(ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD), pathname)
  ) {
    return 'compose_team';
  }
  if (matchPath(catchAll(FOOTBALL_CLUBS_SLUG), pathname)) {
    return 'club_shop';
  }
  if (matchAnySportPath(catchAll(ANY_SPORT_MY_CLUB_SLUG_CARDS), pathname)) {
    return 'my_club_cards';
  }
  if (matchAnySportPath(catchAll(ANY_SPORT_MY_CARDS_CARDS), pathname)) {
    return 'my_cards';
  }
  if (matchAnySportPath(catchAll(ANY_SPORT_MY_CLUB_SLUG), pathname)) {
    return 'my_club';
  }
  if (matchAnySportPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, pathname)) {
    return 'common_pack_details';
  }
  if (matchAnySportPath(ANY_SPORT_MARKET_COMMON_PACKS, pathname)) {
    return 'common_pack_list';
  }
  if (matchPath(FOOTBALL_ONBOARDING_LEADERBOARD_FIRST_LINEUP, pathname)) {
    return 'market_guidance_checkout';
  }
  if (matchPath(FOOTBALL_ONBOARDING_TUTORIAL, pathname)) {
    return 'market_guidance_first_screen';
  }
  if (matchPath(FOOTBALL_ONBOARDING_CHOOSE_COMPETITION, pathname)) {
    return 'market_guidance_choose_competition';
  }
  return pathname;
};

export const useGetInteractionContext = () => {
  const { pathname, search } = useLocation();
  const loc = useRef({ pathname, search });

  useEffect(() => {
    loc.current = { pathname, search };
  }, [pathname, search]);

  return useCallback((subpath?: string) => {
    return `${getPathname(loc.current)}${subpath ? `_${subpath}` : ''}`;
  }, []);
};
